import React from 'react'

import { Formik } from 'formik'
import queryString from 'query-string'
import { useLocation } from 'react-router-dom'

import { ZariContext } from '_zari/context'

import { Form, Col, Row, Button, Divider, Alert } from '_zari/core'

import { PeriodoContext } from 'contexts/PeriodoContext'

import { OrigenesContabilidadConstant, TiposDocumentoSunatConstant, TiposSocioConstant } from 'constants/index'

import { IdGen, ZrSwal } from 'utils'

import * as AsientosContablesService from 'services/asientosContables.service'

import * as FormGroups from './formGroups'
import * as Sections from './sections'

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'actualizar':
      const salida = state.map(val => {
        if (val._idFila === action.payload._idFila) {
          val = {
            ...val,
            ...action.payload
          }
        }
        return val
      })

      if (action.payload.tipoDocumento === TiposDocumentoSunatConstant.NOTA_CREDITO && action.payload.isChecked) {
        const documentoFiltro = action.payload.infoDocRelacionado

        const indexMatch = salida.findIndex(
          x =>
            x.tipoDocumento === documentoFiltro.tipoDocumento &&
            x.serieDocumento === documentoFiltro.serieDocumento &&
            x.numeroDocumento === documentoFiltro.numeroDocumento
        )

        salida[indexMatch] = {
          ...salida[indexMatch],
          montoPago: Math.abs(action.payload.montoPago)
        }
      }

      return salida
    case 'fill':
      return action.payload.map(x => ({
        ...x,
        _idFila: IdGen.newId()
      }))
    case 'limpiar':
      return []
    default:
      return state
  }
}

const MainForm = ({ resetMainForm }) => {
  const __zariContext = React.useContext(ZariContext)
  const __periodoContext = React.useContext(PeriodoContext)

  const location = useLocation()

  const [__keyDetalles, setKeyDetalles] = React.useState(IdGen.newId())

  const [variasMonedas, setVariasMonedas] = React.useState(false)

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, [])

  const mainFormikRef = React.useRef(null)

  React.useEffect(() => {
    if (detalles.length > 0) {
      const monedas = [...new Set(detalles.filter(x => x.montoPago).map(x => x.moneda))]

      if (monedas.length > 1) {
        setVariasMonedas(true)
      } else {
        setVariasMonedas(false)
      }
    }
  }, [detalles])

  const formInitialValues = React.useMemo(() => {
    let { origen } = queryString.parse(location.search)

    return {
      _idSocio: '',
      codigoSocio: '',
      documentoSocio: '',
      nombreSocio: '',
      tipoDocumentoSocio: '',
      origen: origen,
      tipoSocio: '',
      fechaEmision: ''
    }
  }, [location.search])

  const clickBuscarArchivos = React.useCallback(
    ({ _idSocio, tipoSocio }) => {
      __zariContext.loader.open('Buscando documentos pendientes')
      AsientosContablesService.getPendientesxSocioxOrigen(
        _idSocio,
        tipoSocio === TiposSocioConstant.CLIENTE
          ? OrigenesContabilidadConstant.VENTAS
          : OrigenesContabilidadConstant.COMPRAS
      )
        .then(({ data }) => {
          __zariContext.loader.close()

          if (data && data.length > 0) {
            detallesDispatcher({ type: 'fill', payload: data })
          } else {
            __zariContext.snack('warning', 'El cliente indicado no cuenta con documentos pendientes.')
          }
        })
        .catch(() => {
          __zariContext.snack('error', 'No se pudo obtener los documentos pendientes del cliente.')
          __zariContext.loader.close()
        })
    },
    [__zariContext]
  )

  const refreshKeyDetalles = React.useCallback(() => {
    console.log({ detalles })

    setKeyDetalles(IdGen.newId())
  }, [detalles])

  const onSubmit = React.useCallback(() => {
    const submitData = mainFormikRef.current.values
    const setSubmitting = mainFormikRef.current.setSubmitting

    const formData = {
      ...submitData,
      detalles: detalles
        .filter(x => x.montoPago)
        .map(x => ({
          _id: x._id,
          montoPago: parseFloat(x.montoPago)
        }))
    }

    AsientosContablesService.nuevoAsientoContableOgDiarioAplicacionNota(formData)
      .then(({ data }) => {
        ZrSwal.showAlert({
          tipo: 'success',
          mensaje: (
            <>
              <span>Operaciones Generadas :</span> <br />
              {data.operacionesGeneradas.map((x, k) => (
                <React.Fragment key={k}>
                  <span>
                    {k + 1}. {x}
                  </span>{' '}
                  <br />
                </React.Fragment>
              ))}
            </>
          ),
          onSuccess: () => {
            resetMainForm()
          }
        })
      })
      .catch(({ data: { error } }) => {
        __zariContext.snack('error', error)
        setSubmitting(false)
      })
      .finally(() => {
        __periodoContext.actualizarEstados()
      })
  }, [__zariContext, __periodoContext, detalles, resetMainForm])

  return (
    <>
      <Row>
        <Col md={8}>
          <Formik
            innerRef={mainFormikRef}
            initialValues={formInitialValues}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
          >
            {({ errors, touched, isSubmitting, values, setValues }) => (
              <>
                <Form>
                  <Row>
                    <Col md={4}>
                      <FormGroups.TipoSocioFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.tipoSocio}
                        iTouched={touched.tipoSocio}
                      />
                    </Col>
                    <Col md={4}>
                      <FormGroups.FechaEmisionFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.fechaEmision}
                        iTouched={touched.fechaEmision}
                      />
                    </Col>
                  </Row>
                  {values.tipoSocio && (
                    <Row>
                      <Col md={8}>
                        <FormGroups.SocioFormGroup
                          fIsSubmitting={isSubmitting}
                          iError={errors._idSocio}
                          iTouched
                          iValue={values.nombreSocio}
                          setValues={setValues}
                          values={values}
                          dispatch={detallesDispatcher}
                          vTipoSocio={values.tipoSocio}
                        />
                      </Col>
                      <Col md={4}>
                        <Button onClick={() => clickBuscarArchivos(values)} block>
                          Ver Documentos
                        </Button>
                      </Col>
                    </Row>
                  )}
                </Form>
              </>
            )}
          </Formik>
          {detalles.length > 0 && (
            <>
              <Divider />
              <Sections.DocumentosPendientesSection
                key={__keyDetalles}
                filas={detalles}
                dispatch={detallesDispatcher}
                refreshKeyDetalles={refreshKeyDetalles}
              />
              {variasMonedas && <Alert color='danger'>No se puede pagar documentos con distinta moneda.</Alert>}
              <Row className='mt-2'>
                <Col md={12} className='text-right'>
                  <Button onClick={onSubmit}>Procesar</Button>
                </Col>
              </Row>
            </>
          )}
        </Col>
      </Row>
    </>
  )
}

export default React.memo(MainForm)
